import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import {
  getTimeDataByModeApi,
  fetchDayWeekData,
} from '../../store/actions/TimeManagementActions';
import Table from '../shared/TailwindComponents/Table';
import dayjs from 'dayjs';
import { showTimekeepingSidePanel } from '../../store/actions/sidePanelActions';
import { getIsAdmin } from '../../utils/authUtils';

const TimeKeepingLogs = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [mode, setMode] = useState<'day' | 'week' | 'month'>('day');
  const [timeEntries, setTimeEntries] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10; 
  const searchTerm: string = '';
  const selectedFilters: { [key: string]: string[] } = {};
  const sortOption: string = '';
  const isAdmin = getIsAdmin();

  useEffect(() => {
    const fetchData = async () => {
      const data = await dispatch(getTimeDataByModeApi(mode));
      setTimeEntries(data);
    };
    fetchData();
  }, [dispatch, mode]);

  const filteredEntries = timeEntries
    .filter((entry) => {
      if (searchTerm) {
        return (
          entry.Description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          entry.TaskTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          entry.CaseType?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      return true;
    })
    .filter((entry) => {
      let matches = true;
      Object.keys(selectedFilters).forEach((filterKey) => {
        if (selectedFilters[filterKey].length > 0) {
          if (!selectedFilters[filterKey].includes(entry[filterKey])) {
            matches = false;
          }
        }
      });
      return matches;
    })
    .sort((a, b) => {
      if (sortOption === 'DurationAsc') {
        return a.Duration - b.Duration;
      } else if (sortOption === 'DurationDesc') {
        return b.Duration - a.Duration;
      }
      return 0;
    });

    const handleRowClick = async (entry: any) => {
      let date = '';
      let from_date = '';
      let to_date = '';
  
      if (mode === 'day') {
        date = entry.date;
      } else if (mode === 'week') {
        from_date = entry.week_start;
        to_date = dayjs(entry.week_start).add(6, 'day').format('YYYY-MM-DD');
      } else if (mode === 'month') {
        from_date = `${entry.year}-${String(entry.month).padStart(2, '0')}-01`;
        to_date = dayjs(from_date).endOf('month').format('YYYY-MM-DD');
      }
  
      const resp: any = await dispatch(fetchDayWeekData(mode, date, from_date, to_date));
      if (resp) {
        dispatch(showTimekeepingSidePanel(mode, resp.data, false, {}));
      }
    };
  

  const totalPages = Math.ceil(filteredEntries.length / entriesPerPage);
  const displayedEntries = filteredEntries.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  // Columns for the table
  const columns = mode === 'day'
    ? [t('Day'), t('Date'), t('Duration'), t('Description')]
    : mode === 'week'
      ? [t('Week Number'), t('Start of Week'), t('End of Week'), t('Duration'), t('Description'), t('User Name')]
      : [t('Month'), t('Year'), t('Duration'), t('Description'), t('User Name')];

  return (
    <div className="container mx-auto p-6">
      <div className="flex space-x-4 my-4">
        {['day', 'week', 'month'].map((period) => (
          <button
            key={period}
            onClick={() => setMode(period as 'day' | 'week' | 'month')}
            className={`px-4 py-2 rounded-full transition ${mode === period
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
          >
            {t(period.charAt(0).toUpperCase() + period.slice(1))}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between mb-4">
      </div>

      <Table
        columns={columns}
        data={displayedEntries.map((entry) => {
          const rowData: any = {};
          if (isAdmin) {
            rowData[t('Employee Name')] = entry.user_name;
          }
          if (mode === 'day') {
            rowData[t('Day')] = new Date(entry.date).toLocaleDateString('en-US', { weekday: 'long' });
            rowData[t('Date')] = new Date(entry.date).toLocaleDateString();
          } else if (mode === 'week') {
            rowData[t('Week Number')] = entry.week;
            rowData[t('Start of Week')] = new Date(entry.week_start).toLocaleDateString();
            rowData[t('End of Week')] = dayjs(entry.week_start).add(6, 'day').format('YYYY-MM-DD');
          } else if (mode === 'month') {
            rowData[t('Year')] = entry.year;
            rowData[t('Month')] = new Date(entry.year, entry.month - 1).toLocaleString('default', {
              month: 'long',
            });
          }
          rowData[t('Duration')] = `${entry.duration} ${t('hours')}`;
          rowData[t('Description')] = entry.description || t('No description provided');
          rowData['onClick'] = () => handleRowClick(entry);
          return rowData;
        })}
      />

    </div>
  );
};

export default TimeKeepingLogs;
